import { useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { AiAdminConfig, AiFeedbackRating, StaffAiFeedback, StaffRole } from "@backstory/protocol";
import { setApiOrg, setApiProject } from "../../api";
import { useAuth } from "../../auth/store";
import { ErrorState } from "../../components/States";
import { AutoLoadMore, BusyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, SearchSelect, useToast, type Column } from "../../components/ui";
import { formatDateTime, formatRelative } from "../../lib/format";
import { platformAiAdmin } from "../ai-admin/api";
import { staffApi, staffKeys } from "./api";
import "./staff.css";

const PAGE_SIZE = 50;
const ratings = [
  { value: "down", label: "Not helpful" },
  { value: "up", label: "Helpful" },
  { value: "all", label: "All open" },
  { value: "acknowledged", label: "Acknowledged" },
];

type FeedbackFilter = AiFeedbackRating | "all" | "acknowledged";
const reasonLabels: Record<string, string> = {
  wrong_facts: "Wrong facts",
  missed_issue: "Missed the issue",
  not_useful: "Not useful",
};
const kindLabels: Record<string, string> = {
  summary: "Summary",
  explain: "Explanation",
  repro: "Repro steps",
  cluster_explanation: "Issue group",
  pr_draft: "Change draft",
  funnel_explanation: "Funnel",
  digest: "Digest",
  chat: "Chat",
  system_issue_investigation: "System issue",
};

function kindLabel(kind: string): string {
  return kindLabels[kind] ?? kind.replaceAll("_", " ");
}

const VENDORS: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google_ai: "Google AI",
  vertex_ai: "Vertex AI",
  bedrock: "Amazon Bedrock",
  azure: "Azure AI",
};

function vendorName(kind: string | undefined): string {
  if (!kind || kind === "litellm" || kind === "backstory") return "";
  return VENDORS[kind] ?? kind;
}

/**
 * A stored model is often the route that was asked ("standard"), not the
 * account that answered. The route's first target is that account: it is the
 * one that serves the call unless it fails over.
 */
function servedBy(item: StaffAiFeedback, config: AiAdminConfig | undefined): { vendor: string; model: string } {
  const route = config?.routes.find((r) => r.class === item.model);
  const target = route?.targets[0];
  if (target) {
    const kind = config?.providers.find((p) => p.id === target.providerConnectionId)?.provider;
    return { vendor: vendorName(kind), model: target.model };
  }
  return { vendor: vendorName(item.provider), model: item.model ?? "" };
}

function canRequestAccess(role: StaffRole | undefined): boolean {
  return role === "operator" || role === "admin" || role === "owner";
}

/**
 * Human review queue for ratings left on generated answers. Opening the session
 * uses the same membership or support grant as everywhere else; a rating never
 * grants it, and it never changes models or prompts.
 */
export function StaffAiFeedbackPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const role = useAuth().me?.user.staffRole;
  const [rating, setRating] = useState<FeedbackFilter>("down");
  const [requesting, setRequesting] = useState<StaffAiFeedback | null>(null);
  const queue = rating === "acknowledged" ? "acknowledged" : "open";
  const feedback = useInfiniteQuery({
    queryKey: staffKeys.aiFeedback(rating),
    queryFn: ({ pageParam }) =>
      staffApi.aiFeedback({
        rating: rating === "acknowledged" ? "all" : rating,
        queue,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, page) => n + page.items.length, 0);
      return last.items.length === PAGE_SIZE && loaded < last.total ? loaded : undefined;
    },
  });
  const rows = feedback.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = feedback.data?.pages[0];
  const openCounts =
    counts && typeof counts.up === "number" && typeof counts.down === "number"
      ? `${counts.down.toLocaleString()} not helpful · ${counts.up.toLocaleString()} helpful`
      : null;
  const routes = useQuery({
    queryKey: platformAiAdmin.keys.config,
    queryFn: () => platformAiAdmin.config(),
    staleTime: 60_000,
  });

  const openSession = (item: StaffAiFeedback) => {
    if (!item.sessionId || item.sessionAccess !== "open") return;
    setApiOrg(item.orgId);
    if (item.projectId) setApiProject(item.projectId);
    void navigate({
      to: "/staff/orgs/$org/sessions/$id",
      params: { org: item.orgId, id: item.sessionId },
      state: { sessionReturn: { to: "/staff/ai-feedback", label: "AI feedback" } } as never,
    });
  };

  const columns: Column<StaffAiFeedback>[] = [
    {
      key: "rating",
      header: "Rating",
      label: "Rating",
      className: "ai-feedback-rating",
      render: (item) => (
        <div className="ai-feedback-verdict">
          <span className={`badge ${item.rating === "down" ? "warn" : "ok"}`}>{item.rating === "down" ? "Not helpful" : "Helpful"}</span>
          {item.reason && <span className="ai-feedback-reason">{reasonLabels[item.reason] ?? item.reason}</span>}
        </div>
      ),
    },
    {
      key: "artifact",
      header: "Answer",
      label: "Answer",
      className: "ai-feedback-answer",
      render: (item) => (
        <div>
          <div>{kindLabel(item.kind)}</div>
          {item.promptVersion && <div className="muted small">{item.promptVersion}</div>}
        </div>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      label: "Customer",
      render: (item) => item.orgName || item.orgId,
    },
    {
      key: "model",
      header: "Model",
      label: "Model",
      render: (item) => {
        const served = servedBy(item, routes.data);
        if (!served.vendor && !served.model) return <span className="muted">—</span>;
        return <span className="ai-feedback-model">{[served.vendor, served.model].filter(Boolean).join(" · ")}</span>;
      },
    },
    {
      key: "by",
      header: "Submitted by",
      label: "Submitted by",
      render: (item) => item.feedbackByEmail || item.feedbackBy || <span className="muted">—</span>,
    },
    {
      key: "when",
      header: "Submitted",
      label: "Submitted",
      render: (item) => <span title={formatDateTime(item.feedbackAt)}>{formatRelative(item.feedbackAt)}</span>,
    },
    {
      key: "act",
      header: "",
      className: "ai-feedback-actions",
      render: (item) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <FeedbackMenu
            item={item}
            canWrite={canRequestAccess(role)}
            acknowledged={queue === "acknowledged"}
            onOpen={() => openSession(item)}
            onRequest={() => setRequesting(item)}
            onChanged={() => void qc.invalidateQueries({ queryKey: ["staff", "ai-feedback"] })}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="AI feedback">
        <div className="toolbar customers-filters" aria-label="Feedback filters">
          <SearchSelect
            value={rating}
            options={ratings}
            onChange={(value) => setRating(value as FeedbackFilter)}
            aria-label="Rating"
            searchPlaceholder="Search ratings…"
          />
          {openCounts && <span className="muted small">{openCounts}</span>}
        </div>
      </PageHeader>

      {feedback.isError ? (
        <ErrorState error={feedback.error} retry={() => void feedback.refetch()} />
      ) : (
        <div className="card stack-table ai-feedback-table">
          <DataTable
            ariaLabel="AI feedback"
            rows={rows}
            rowKey={(item) => item.artifactId}
            loading={feedback.isPending}
            loadingMore={feedback.isFetchingNextPage}
            onRowClick={openSession}
            rowClickable={(item) => item.sessionAccess === "open" && !!item.sessionId}
            empty={queue === "acknowledged" ? "Nothing has been acknowledged." : "No answers have this rating yet."}
            columns={columns}
          />
          <AutoLoadMore hasMore={!!feedback.hasNextPage} loading={feedback.isFetchingNextPage} loadMore={() => void feedback.fetchNextPage()} label="Loading more ratings" />
        </div>
      )}
      {requesting && (
        <RequestReviewAccess
          item={requesting}
          onClose={() => setRequesting(null)}
          onSent={() => {
            setRequesting(null);
            void qc.invalidateQueries({ queryKey: ["staff", "ai-feedback"] });
            toast.push("success", "Access requested. The customer has to accept it.");
          }}
        />
      )}
    </div>
  );
}

function FeedbackMenu({
  item,
  canWrite,
  acknowledged,
  onOpen,
  onRequest,
  onChanged,
}: {
  item: StaffAiFeedback;
  canWrite: boolean;
  acknowledged: boolean;
  onOpen: () => void;
  onRequest: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const acknowledge = useMutation({
    mutationFn: () => staffApi.acknowledgeAiFeedback(item.artifactId),
    onSuccess: () => {
      setConfirming(false);
      toast.push("success", "Acknowledged. It comes back if they rate this answer again.");
      onChanged();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const reopen = useMutation({
    mutationFn: () => staffApi.reopenAiFeedback(item.artifactId),
    onSuccess: () => {
      toast.push("success", "Back in the queue.");
      onChanged();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const access = item.sessionAccess ?? "none";
  const sessionAction = !!item.sessionId && (access === "open" || access === "pending" || access === "limited" || (access === "none" && canWrite));
  if (!sessionAction && !canWrite) return null;
  const who = item.orgName || "this customer";
  return (
    <>
      <RowMenu label={`Actions for ${kindLabel(item.kind)}`}>
        {item.sessionId && access === "open" && (
          <button type="button" role="menuitem" onClick={onOpen}>Open session</button>
        )}
        {item.sessionId && access === "pending" && (
          <Link to="/staff/orgs/$org" params={{ org: item.orgId }} search={{ tab: "access" }} role="menuitem">
            Waiting for the customer
          </Link>
        )}
        {item.sessionId && access === "limited" && (
          <Link to="/staff/orgs/$org" params={{ org: item.orgId }} search={{ tab: "access" }} role="menuitem">
            Access does not include sessions
          </Link>
        )}
        {item.sessionId && access === "none" && canWrite && (
          <button type="button" role="menuitem" onClick={onRequest}>Request access</button>
        )}
        {canWrite && (acknowledged ? (
          <button type="button" role="menuitem" onClick={() => reopen.mutate()} disabled={reopen.isPending}>Reopen</button>
        ) : (
          <button type="button" role="menuitem" onClick={() => setConfirming(true)}>Acknowledge</button>
        ))}
      </RowMenu>
      <ConfirmDialog
        open={confirming}
        title="Acknowledge this rating?"
        confirmLabel={acknowledge.isPending ? "Acknowledging…" : "Acknowledge"}
        busy={acknowledge.isPending}
        onClose={() => !acknowledge.isPending && setConfirming(false)}
        onConfirm={() => acknowledge.mutate()}
      >
        The {kindLabel(item.kind).toLowerCase()} for <strong>{who}</strong> leaves the queue. It comes back if they rate this answer again.
      </ConfirmDialog>
    </>
  );
}

function RequestReviewAccess({ item, onClose, onSent }: { item: StaffAiFeedback; onClose: () => void; onSent: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState(`Review feedback on a ${kindLabel(item.kind).toLowerCase()}`);
  const [hours, setHours] = useState("4");
  const request = useMutation({
    mutationFn: () =>
      staffApi.requestAccess(item.orgId, {
        reason: reason.trim(),
        scopes: ["read_sessions", "read_settings"],
        ttlHours: Number(hours) || 4,
      }),
    onSuccess: onSent,
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const h = Number(hours);
  const hoursError = hours !== "" && (!Number.isFinite(h) || h <= 0 || h > 168) ? "Between 1 and 168 hours." : undefined;
  const valid = reason.trim().length > 2 && !hoursError && hours !== "";
  return (
    <Dialog
      open
      onClose={() => !request.isPending && onClose()}
      title="Request access to review"
      subtitle={`${item.orgName || "The customer"} decides whether to accept. The grant is only for viewing.`}
      width={520}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={request.isPending}>Cancel</button>
          <BusyButton className="btn primary" busy={request.isPending} busyLabel="Sending…" disabled={!valid} onClick={() => request.mutate()}>
            Send request
          </BusyButton>
        </>
      }
    >
      <Field label="Reason" hint="The customer sees this.">
        {(p) => <input {...p} className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />}
      </Field>
      <Field label="For how long" error={hoursError}>
        {(p) => (
          <select {...p} className="input" value={hours} onChange={(e) => setHours(e.target.value)}>
            <option value="1">1 hour</option>
            <option value="4">4 hours</option>
            <option value="24">1 day</option>
            <option value="72">3 days</option>
            <option value="168">7 days</option>
          </select>
        )}
      </Field>
    </Dialog>
  );
}
